import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { MuiTelInput } from 'mui-tel-input';
import { FunctionComponent } from 'react';
import { Controller } from 'react-hook-form';

import {
  FolkloreType,
  FoodType,
  User,
} from '../../../../../apollo/__generated__/graphql';
import { useUserInfosForm } from './useUserInfosForm';

interface UserInfosFormProps {
  currentUser: User;
}

export const UserInfosForm: FunctionComponent<UserInfosFormProps> = ({
  currentUser,
}) => {
  const {
    birthdateValue,
    errors,
    externalReferentPhoneValue,
    folkloreValue,
    isDirty,
    onBirthdateChange,
    onExternalReferentPhoneChange,
    onPhoneChange,
    onSubmit,
    phoneValue,
    register,
    control,
  } = useUserInfosForm(currentUser);
  return (
    <form onSubmit={onSubmit}>
      <TextField
        margin="normal"
        fullWidth
        id="firstname"
        label="Prénom"
        placeholder="Michel"
        autoFocus
        type="text"
        {...register('firstname')}
        helperText={
          errors.firstname?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.firstname.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        fullWidth
        id="lastname"
        label="Nom"
        placeholder="Dupont"
        type="text"
        {...register('lastname')}
        helperText={
          errors.lastname?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.lastname.message}
            </Typography>
          ) : null
        }
      />

      <Box>
        <Typography variant="body2">Date de naissance</Typography>
        <DatePicker
          value={birthdateValue}
          onChange={onBirthdateChange}
          sx={{ width: '100%' }}
        />
        {errors.birthdate?.message ? (
          <Typography component="p" variant="body2" color="error.main">
            {errors.birthdate.message}
          </Typography>
        ) : null}
      </Box>

      <FormControl fullWidth id="folklore">
        <FormLabel>Folklore</FormLabel>
        <Controller
          name="folklore"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup
              value={value}
              onChange={(_, value) => {
                onChange(value as FolkloreType);
              }}
            >
              <FormControlLabel
                value={FolkloreType.Faluchard}
                control={<Radio />}
                label="Faluchard"
              />
              <FormControlLabel
                value={FolkloreType.Impetrant}
                control={<Radio />}
                label="Impétrant"
              />
              <FormControlLabel
                value={FolkloreType.Sympathisant}
                control={<Radio />}
                label="Sympathisant"
              />
              <FormControlLabel
                value={FolkloreType.Other}
                control={<Radio />}
                label="Autre"
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      {errors.folklore?.message ? (
        <Typography component="p" variant="body2" color="error.main">
          {errors.folklore.message}
        </Typography>
      ) : null}
      {folkloreValue === FolkloreType.Faluchard ||
      folkloreValue === FolkloreType.Other ? (
        <TextField
          margin="normal"
          fullWidth
          id="nickname"
          label="Surnom"
          placeholder="Hubert bonisseur de la bath"
          type="text"
          {...register('nickname')}
          helperText={
            errors.nickname?.message ? (
              <Typography component="p" variant="body2" color="error.main">
                {errors.nickname.message}
              </Typography>
            ) : null
          }
        />
      ) : null}
      <TextField
        margin="normal"
        fullWidth
        id="studyTown"
        label="Ville d'étude"
        placeholder="Troyes"
        type="text"
        {...register('studyTown')}
        helperText={
          errors.studyTown?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.studyTown.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        fullWidth
        id="studies"
        label="Études"
        placeholder="Ingénieur"
        type="text"
        {...register('studies')}
        helperText={
          errors.studies?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.studies.message}
            </Typography>
          ) : null
        }
      />
      <MuiTelInput
        margin="normal"
        fullWidth
        id="phone"
        label="Numéro de téléphone"
        helperText={
          errors.phone?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.phone.message}
            </Typography>
          ) : null
        }
        onChange={onPhoneChange}
        value={phoneValue}
        defaultCountry="FR"
      />
      <TextField
        margin="normal"
        fullWidth
        id="externalReferentFirstname"
        label="Prénom du référent extérieur"
        placeholder="Michel"
        autoFocus
        type="text"
        {...register('externalReferentFirstname')}
        helperText={
          errors.externalReferentFirstname?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.externalReferentFirstname.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        fullWidth
        id="externalReferentLastname"
        label="Nom du référent extérieur"
        placeholder="Dupont"
        type="text"
        {...register('externalReferentLastname')}
        helperText={
          errors.externalReferentLastname?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.externalReferentLastname.message}
            </Typography>
          ) : null
        }
      />
      <MuiTelInput
        margin="normal"
        fullWidth
        id="externalReferentPhone"
        label="Numéro de téléphone du référent extérieur"
        helperText={
          errors.externalReferentPhone?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.externalReferentPhone.message}
            </Typography>
          ) : null
        }
        onChange={onExternalReferentPhoneChange}
        value={externalReferentPhoneValue}
        defaultCountry="FR"
      />

      <FormControl fullWidth id="food">
        <FormLabel sx={{ mt: 2 }}>Régime alimentaire</FormLabel>
        <Controller
          name="food"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RadioGroup
              value={value}
              onChange={(_, value) => {
                onChange(value as FoodType);
              }}
            >
              <FormControlLabel
                value={FoodType.Omnivore}
                control={<Radio />}
                label="Omnivore"
              />
              <FormControlLabel
                value={FoodType.Vegetarian}
                control={<Radio />}
                label="Végétarien"
              />
              <FormControlLabel
                value={FoodType.Vegan}
                control={<Radio />}
                label="Végan"
              />
              <FormControlLabel
                value={FoodType.Other}
                control={<Radio />}
                label="Autre"
              />
            </RadioGroup>
          )}
        />
      </FormControl>
      {errors.food?.message ? (
        <Typography component="p" variant="body2" color="error.main">
          {errors.food.message}
        </Typography>
      ) : null}
      <TextField
        margin="normal"
        fullWidth
        id="allergies"
        label="Allergies"
        placeholder="Ex: Gluten, Arachides"
        type="text"
        {...register('allergies')}
        helperText={
          errors.allergies?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.allergies.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        fullWidth
        id="medication"
        label="Médicaments"
        placeholder="Ex: Aspirine, Doliprane"
        type="text"
        {...register('medication')}
        helperText={
          errors.medication?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.medication.message}
            </Typography>
          ) : null
        }
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={!isDirty}
      >
        Enregistrer
      </Button>
    </form>
  );
};
